// AriaShell — the persistent ARIA presence layer.
//
// Mounted at the app root. Provides:
//   - Floating ARIA visualizer button (bottom-right corner on desktop,
//     bottom-center on mobile) that opens the command bar
//   - Global Cmd+K binding via AriaCommandBar
//   - Live message ribbon when aria.lastMessage is set (dismissible)
//
// The shell is invisible when there's nothing to show — it renders
// only the persistent visualizer button by default. When ARIA has
// something to say, the message ribbon slides in above the button.

import React, { useEffect } from 'react';
import { sixfigure, textStyle, motionDuration } from '../lib/tokens';
import { useAria } from './AriaContext';
import AriaStateVisualizer from './AriaStateVisualizer';
import AriaCommandBar from './AriaCommandBar';

const CSS = `
@keyframes aria-msg-slide-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
.aria-msg-ribbon {
  animation: aria-msg-slide-in 260ms cubic-bezier(0.2, 0.0, 0.0, 1.0) forwards;
}
@media (prefers-reduced-motion: reduce) {
  .aria-msg-ribbon { animation: none; opacity: 1; transform: none; }
}
/* Single canonical bottom-right floating launcher. Consolidated here
   after retiring the duplicate NovaOrb + purple "AI" ChatPanel corner
   mounts (they overlapped this pill). Safe-area aware so it never
   collides with a device's home-indicator / notch on mobile. */
.aria-shell-dock {
  position: fixed;
  right: max(24px, env(safe-area-inset-right));
  bottom: max(24px, env(safe-area-inset-bottom));
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 14px;
  pointer-events: none;
}
@media (max-width: 640px) {
  .aria-shell-dock {
    right: max(16px, env(safe-area-inset-right));
    bottom: max(16px, env(safe-area-inset-bottom));
  }
}
`;

// Hook to reflect current tab into ARIA's context. Consumers of
// setTab should also call aria.setContext(newTab). Providing this
// helper as a hook lets any surface push context updates simply.
export function useAriaTabContext(currentTab) {
  const aria = useAria();
  useEffect(() => {
    if (currentTab) aria.setContext(currentTab);
  }, [currentTab, aria]);
}

export default function AriaShell() {
  const { lastMessage, setMessage, openCommandBar } = useAria();
  const [hidden, setHidden] = React.useState(() => {
    try { return localStorage.getItem('aria_launcher_hidden') === '1'; } catch { return false; }
  });
  const hide = () => { try { localStorage.setItem('aria_launcher_hidden', '1'); } catch { /* */ } setHidden(true); };

  // Hidden: the command bar (⌘K) still works — we just drop the floating pill
  // that subscribers found intrusive. Nothing covers their content.
  if (hidden) {
    return (<><style>{CSS}</style><AriaCommandBar/></>);
  }

  return (
    <>
      <style>{CSS}</style>
      <AriaCommandBar/>
      <div className="aria-shell-dock">
        {/* Message ribbon */}
        {lastMessage && (
          <div
            className="aria-msg-ribbon"
            style={{
              pointerEvents: 'auto',
              maxWidth: 380,
              padding: sixfigure.space.md,
              background: sixfigure.commandBar.background,
              color: '#f8fafc',
              borderRadius: sixfigure.radius.card,
              border: `1px solid ${sixfigure.commandBar.borderTop}`,
              boxShadow: sixfigure.glow.intelligence,
              display: 'flex', alignItems: 'flex-start', gap: sixfigure.space.sm,
            }}
          >
            <span style={{ ...textStyle('eyebrow'), color: sixfigure.accent.ai.fg }}>
              ARIA
            </span>
            <div style={{ ...textStyle('bodySm'), flex: 1 }}>
              {lastMessage}
            </div>
            <button
              onClick={() => setMessage(null)}
              aria-label="Dismiss ARIA message"
              style={{
                background: 'transparent', border: 'none',
                color: '#94a3b8', cursor: 'pointer', fontSize: 14,
                padding: 0, lineHeight: 1,
              }}
            >×</button>
          </div>
        )}

        {/* Compact orb launcher — small footprint so it doesn't cover content.
            Click opens the command bar; ⌘K works from anywhere. A hover ×
            hides it entirely (persisted); subscribers who don't want it gone-
            for-good keep the keyboard shortcut. */}
        <div
          style={{ pointerEvents: 'auto', position: 'relative' }}
          onMouseEnter={(e) => { const x = e.currentTarget.querySelector('.aria-hide-x'); if (x) x.style.opacity = '1'; }}
          onMouseLeave={(e) => { const x = e.currentTarget.querySelector('.aria-hide-x'); if (x) x.style.opacity = '0'; }}
        >
          <button
            onClick={openCommandBar}
            title={`Ask ARIA · ${navigator.platform.includes('Mac') ? '⌘K' : 'Ctrl+K'}`}
            aria-label="Ask ARIA"
            style={{
              width: 44, height: 44, borderRadius: '50%',
              background: sixfigure.commandBar.background,
              border: `1px solid ${sixfigure.commandBar.borderTop}`,
              boxShadow: sixfigure.glow.ariaActive,
              display: 'grid', placeItems: 'center', padding: 0,
              cursor: 'pointer',
              transition: `all ${motionDuration('quick')}ms ${sixfigure.motion.easing.standard}`,
            }}
          >
            <AriaStateVisualizer size={28} onClick={openCommandBar}/>
          </button>
          <button
            className="aria-hide-x"
            onClick={(e) => { e.stopPropagation(); hide(); }}
            title="Hide ARIA (⌘K still works)"
            aria-label="Hide ARIA launcher"
            style={{
              position: 'absolute', top: -6, right: -6,
              width: 18, height: 18, borderRadius: '50%',
              background: '#1a1f2b', border: '1px solid rgba(148,163,184,0.4)',
              color: '#cbd5e1', fontSize: 11, lineHeight: 1, cursor: 'pointer',
              display: 'grid', placeItems: 'center', padding: 0,
              opacity: 0, transition: 'opacity 140ms ease',
            }}
          >×</button>
        </div>
      </div>
    </>
  );
}
